
import { Link, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { Loader2, ShieldOff } from 'lucide-react';
import { isAdminHost } from '../../lib/host';
import { firstAccessibleStaffPath, type StaffPermission } from '../../lib/access-control';

interface ProtectedRouteProps {
    allowedRoles?: ('customer' | 'admin' | 'employee')[];
    requiredPermission?: StaffPermission;
}

function AdminAccessDenied() {
    const { signOut, permissions } = useAuth();
    const accessiblePath = firstAccessibleStaffPath(permissions);
    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-950">
            <div className="text-center max-w-sm mx-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 mb-6">
                    <ShieldOff size={32} className="text-red-400" />
                </div>
                <h1 className="text-xl font-bold text-white mb-2">Access Denied</h1>
                <p className="text-slate-400 text-sm mb-6">
                    Your account does not have permission to access the Staff Portal. Contact an administrator if you believe this is an error.
                </p>
                <div className="flex items-center justify-center gap-2">
                    {accessiblePath && (
                        <Link to={accessiblePath} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-xl transition-all">
                            Go to my portal
                        </Link>
                    )}
                    <button onClick={signOut} className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white text-sm font-medium rounded-xl transition-all">
                        Sign Out
                    </button>
                </div>
            </div>
        </div>
    );
}

export function ProtectedRoute({ allowedRoles, requiredPermission }: ProtectedRouteProps) {
    const { session, loading, userRole, hasPermission } = useAuth();

    if (loading) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-slate-50">
                <Loader2 className="animate-spin text-brand" size={48} />
            </div>
        );
    }

    if (!session) {
        return <Navigate to="/login" replace />;
    }

    // Role check
    if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
        if (isAdminHost()) {
            return <AdminAccessDenied />;
        }
        console.warn(`Access denied. User role '${userRole}' not in allowed roles:`, allowedRoles);
        return <Navigate to="/" replace />;
    }

    if (requiredPermission && !hasPermission(requiredPermission)) {
        if (isAdminHost()) return <AdminAccessDenied />;
        return <Navigate to="/" replace />;
    }

    return <Outlet />;
}
